import { useHasRole } from "@p0security/frontend-core";
import { useCallback, useContext } from "react";

import { CONFIG_ROUTES_ROLES } from "../App/roles";
import { AgenticClientsContext } from "./AgenticClientsContext";
import {
  useMcpClientRegistration,
  useMcpClientUpdate,
  useMcpServers,
} from "./hooks";
import { AgenticOverview } from "./overview/AgenticOverview";
import type { AgenticClientRegistration } from "./types";

/** Tenant-level view of MCP clients, servers, and gateway events. */
export const AgenticPage: React.FC = () => {
  const canManageServers = useHasRole(...CONFIG_ROUTES_ROLES);
  const canRegisterClients = useHasRole("viewer");
  const { refresh: refreshClients } = useContext(AgenticClientsContext);
  const {
    error: serversError,
    items: servers,
    loading: serversLoading,
    refresh: refreshServers,
  } = useMcpServers();
  const {
    clearError: clearRegistrationError,
    error: registrationError,
    loading: isRegistering,
    register,
  } = useMcpClientRegistration();
  const {
    clearError: clearUpdateError,
    error: updateError,
    loading: isUpdatingClient,
    update: updateClient,
  } = useMcpClientUpdate();

  const handleRegister = useCallback(
    async (values: AgenticClientRegistration) => {
      const client = await register(values);
      if (client) refreshClients();
      return client;
    },
    [refreshClients, register]
  );

  const handleUpdateClient = useCallback(
    async (...args: Parameters<typeof updateClient>) => {
      const updated = await updateClient(...args);
      if (updated) refreshClients();
      return updated;
    },
    [refreshClients, updateClient]
  );

  // The overview lays out the tab menu and renders the active tab's route in its
  // own `<Outlet>`; data flows to the tabs through that outlet's context.
  return (
    <AgenticOverview
      canManageServers={canManageServers}
      canRegisterClients={canRegisterClients}
      clientUpdateError={updateError}
      isRegistering={isRegistering}
      isUpdatingClient={isUpdatingClient}
      onClearClientUpdateError={clearUpdateError}
      onClearRegistrationError={clearRegistrationError}
      onRefreshServers={refreshServers}
      onRegister={handleRegister}
      onUpdateClient={handleUpdateClient}
      registrationError={registrationError}
      servers={servers}
      serversError={serversError}
      serversLoading={serversLoading}
    />
  );
};
